import { getBearerToken } from "../../../../api/postCall/bearerToken";
import {
  searchForMedicalPaOrdersFromPostgres,
  SearchForMedicalPaOrdersFromPostgresParams,
} from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { PostgresResponseForMedicalPaOrders } from "../../../../data-model/postgresResponseForMedicalPaOrders";
import { MyMedicalPaOrdersTableData } from "../../../../data-model/tablesData";
import { ReviewPendingOrdersState } from "../hooks/useReviewPendingOrdersTableData";
import { generateReviewPendingOrdersTableDataFromAlgoliaRecords } from "../utils/generateTableDataFromAlgoliaRecords";

/**
 * Fetches initial review-pending orders data by executing multiple Postgres search
 * payloads sequentially, combining the results, and updating both the orders state
 * and table data.
 *
 * @param payloads - Array of search parameter objects, one per page to fetch
 * @param authStatusOptions - Available auth status options for badge rendering
 * @param setReviewPendingOrdersState - State setter for the orders loading/data/error state
 * @param setTableData - State setter for the transformed table row data
 * @param setInitialDataFetchingExecuted - Flags that the initial fetch cycle has completed
 */
export const makePostCallForInitialDataFetch = async (
  payloads: SearchForMedicalPaOrdersFromPostgresParams[],
  authStatusOptions: AuthStatusOptionModel[],
  setReviewPendingOrdersState: React.Dispatch<
    React.SetStateAction<ReviewPendingOrdersState>
  >,
  setTableData: React.Dispatch<
    React.SetStateAction<MyMedicalPaOrdersTableData[]>
  >,
  setInitialDataFetchingExecuted: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  setReviewPendingOrdersState((prev) => ({ ...prev, loading: true }));

  try {
    await getBearerToken();

    const data: PostgresResponseForMedicalPaOrders[] = [];

    for (const payload of payloads) {
      const response = await searchForMedicalPaOrdersFromPostgres(payload);
      const parsedResponse = JSON.parse(
        response,
      ) as PostgresResponseForMedicalPaOrders;
      data.push(parsedResponse);
    }

    const combinedData = appendAllResponses(data);

    setReviewPendingOrdersState((prev) => ({
      ...prev,
      data: combinedData,
    }));
    setTableData(
      generateReviewPendingOrdersTableDataFromAlgoliaRecords(
        combinedData.hits ?? [],
        authStatusOptions,
      ),
    );
  } catch (error) {
    setReviewPendingOrdersState((prev) => ({
      ...prev,
      error: error as string,
    }));
  } finally {
    setReviewPendingOrdersState((prev) => ({ ...prev, loading: false }));
    setInitialDataFetchingExecuted(true);
  }
};

/**
 * Merges multiple Postgres response pages into a single response by concatenating their hits arrays.
 *
 * @param responses - Array of paginated Postgres responses to combine
 * @returns A single response with all hits merged and metadata from the last response
 */
const appendAllResponses = (
  responses: PostgresResponseForMedicalPaOrders[],
) => {
  return responses.reduce((acc, response) => {
    return {
      ...response,
      hits: [...(acc.hits ?? []), ...(response.hits ?? [])],
    };
  }, {} as PostgresResponseForMedicalPaOrders);
};
